"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LoggerFactory = void 0;
const datadogPino_js_1 = __importDefault(require("./datadog/datadogPino.js"));
// eslint-disable-next-line import/no-useless-path-segments
const state_cjs_1 = require("../src/state.cjs");
const interpolationRegex = /%(s|d|j|o|O)/;
/**
 * The signature for the previous logging library allowed a user to call the log function with a string ('message'), and an arbitrary list of
 * arguments. These arguments were either used for string interpolation on the message or logged as is. It allowed for logs that
 * looked like:
 *
 *      logger.info('Some important log', data);
 *      logger.error('We throwing this error for x reason', err);
 *
 * With pino, the arbitrary list of arguments is only used for string interpolation, which would cause a fundamental change in behavior that users
 * aren't expecting. For example, if we log an error like the example above, the error (and its stack trace is not logged); only the message will be
 * seen. We are using the logic in this hook to provide the previous behavior in order to make adoption much more manageable.
 */
const hooks = {
    logMethod(args, method, _level) {
        if (args.length >= 2 && typeof args[0] === 'string' && !interpolationRegex.test(args[0])) {
            if (typeof args[1] === 'object') {
                const arg1 = args.shift();
                const arg2 = args.shift();
                return method.apply(this, [arg2, arg1, ...args]);
            }
            // eslint-disable-next-line no-param-reassign
            args[0] = `${args[0]} %s`;
            return method.apply(this, args);
        }
        return method.apply(this, args);
    }
};
class LoggerFactory {
    static initConfig(loggerConfig = { loggerOptions: { level: 'warn' } }) {
        (0, state_cjs_1.initializeConfig)(loggerConfig);
    }
    /**
     * Creates either a browser logger or a server logger based on the runtime environment,
     * 1. Browser Logger - Based on logger config creates a browser logger instance that sends logs to datadog by default
     * or uses the custom transport configured in the logger config, see send function here - https://getpino.io/#/docs/browser?id=transmit-object
     * 2. Server side logger - Creates a pino logger based on the provided options (https://github.com/pinojs/pino/blob/master/docs/api.md#pinooptions-destination--logger)
     * @param name
     * @param options
     */
    static create(name, options) {
        if (typeof window === 'undefined') {
            return this.createLogger(name, options);
        }
        return this.createBrowserLogger(name, options);
    }
    /**
     * Creates a client side logger, by default it is set up to send logs to datadog
     * @param name - name of the logger
     * @param options - pino logger options
     */
    static createBrowserLogger(name, options) {
        const defaultBrowserTransport = (datadogOptions) => {
            if (datadogOptions) {
                return (0, datadogPino_js_1.default)({
                    name,
                    ...datadogOptions
                });
            }
            throw new Error('Default browser transport needs datadog config to be initialized');
        };
        if ((0, state_cjs_1.getConfig)()) {
            const { datadogOptions, loggerOptions = { level: 'warn' }, browserTransport = defaultBrowserTransport(datadogOptions) } = (0, state_cjs_1.getConfig)();
            const { level = 'warn', ...opts } = options || loggerOptions;
            const levelLowercase = level.toLowerCase();
            const browser = {
                // If datadogOptions are being passed, that means we should not log to browser console (because the logs are being sent to DD)
                // The `disabled` and `write` properties are required so that we don't print logs on the browser console
                ...(!!datadogOptions && {
                    disabled: true,
                    write: () => {
                        // Empty write function
                    }
                }),
                transmit: {
                    level: levelLowercase,
                    send: browserTransport
                }
            };
            return this.createLogger(name, { browser, level, ...opts });
        }
        throw new Error('Logger config not initialized, use LoggerFactory.initConfig(...) to initialize');
    }
    /**
     * Creates a pino logger based on the provided options (https://github.com/pinojs/pino/blob/master/docs/api.md#pinooptions-destination--logger)
     * @param name - name of the logger
     * @param options - pino logger options
     */
    static createLogger(name, options) {
        const loggerOptions = (0, state_cjs_1.getConfig)()?.loggerOptions || { level: 'info' };
        const { level = 'warn', browser, ...opts } = options || loggerOptions;
        const logger = (0, state_cjs_1.pino)({
            name,
            level: level.toLowerCase(),
            formatters: {
                level(label) {
                    return { level: label };
                }
            },
            browser,
            hooks,
            onChild: (instance) => {
                // eslint-disable-next-line no-param-reassign
                instance.originalLevel = instance.level;
                (0, state_cjs_1.addLogger)(instance);
            },
            ...opts,
            ...defaultMixin((0, state_cjs_1.getConfig)()?.tracer, opts.mixin)
        });
        logger.originalLevel = level.toLowerCase();
        (0, state_cjs_1.addLogger)(logger);
        return logger;
    }
    /**
     * Set log level for a given logger or set log level globally
     * @param level log level to be set
     * @param loggerName the name of the requested logger or a regex used to identify the logger/loggers, if not provided log level will be changed for all loggers
     */
    static setLogLevel(level, loggerName) {
        (0, state_cjs_1.setLogLevel)(level, loggerName);
    }
    /**
     * Reset log level on loggers affected by log level changes made using `setLogLevel()`.
     */
    static resetLogLevelsToOriginal() {
        (0, state_cjs_1.resetActiveLoggersToOriginalLevel)();
    }
}
exports.LoggerFactory = LoggerFactory;
/**
 * A default mixin provided by the logging library is used to inject datadog context information into
 * logs. The code is setup to work such that if 'dd-trace' is not included in the dependency we don't
 * loose key logging functinlaity.
 *
 */
function defaultMixin(tracer, mixin) {
    return tracer || mixin
        ? {
            mixin: (mergeObject, level) => {
                const context = mixin ? mixin(mergeObject, level) : {};
                if (tracer) {
                    const span = tracer.scope().active();
                    if (span) {
                        tracer.inject(span.context(), 'log', context);
                    }
                }
                return context;
            }
        }
        : {};
}
//# sourceMappingURL=LoggerFactory.js.map